import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";

import { request } from "hooks/useOkoApi/useOkoApi";

const PROVIDERS = ["EDNITION", "EDNITION_EDFI"];
const ENVIRONMENTS = ["UAT", "PROD"];
const OWNER_KINDS = ["school", "client"];

const EMPTY_FORM = {
  provider: "EDNITION",
  environment: "UAT",
  ownerKind: "school",
  ownerId: "",
  base_url: "",
  api_key: "",
  api_secret: "",
};

const EMPTY_EDIT = { base_url: "", api_key: "", api_secret: "" };

/**
 * Tab 1 — Create / edit credentials. Reuses the EXISTING credential endpoints.
 * Secrets (api_key / api_secret) are write-only: they are sent only on POST/PATCH
 * and are never read back (the list shows has_api_secret only). The owner picker
 * shows school/client names but submits the underlying UUID.
 */
export const CreateCredentialTab = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [credentials, setCredentials] = useState([]);
  const [schools, setSchools] = useState([]);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [testResults, setTestResults] = useState({});
  const [testingId, setTestingId] = useState(null);

  const loadCredentials = useCallback(async () => {
    try {
      const data = await request("integrations/credentials", { method: "GET" });
      setCredentials(data?.credentials || []);
    } catch (e) {
      setError(e?.detail || "Failed to load credentials");
    }
  }, []);

  const loadOwners = useCallback(async () => {
    try {
      const [schoolData, clientData] = await Promise.all([
        request("admin/schools", { method: "GET" }),
        request("admin/clients", { method: "GET" }),
      ]);
      setSchools(schoolData?.schools || []);
      setClients(clientData?.clients || []);
    } catch (e) {
      setError(e?.detail || "Failed to load schools/clients");
    }
  }, []);

  useEffect(() => {
    loadCredentials();
    loadOwners();
  }, [loadCredentials, loadOwners]);

  const setField = (name) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const setEditField = (name) => (event) => {
    const { value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const owners = form.ownerKind === "school" ? schools : clients;

  const ownerName = useCallback(
    (credential) => {
      if (credential.school_id) {
        const match = schools.find((s) => s.id === credential.school_id);
        return match ? match.name : credential.school_id;
      }
      if (credential.client_id) {
        const match = clients.find((c) => c.id === credential.client_id);
        return match ? match.name : credential.client_id;
      }
      return "";
    },
    [schools, clients]
  );

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    const payload = {
      provider: form.provider,
      environment: form.environment,
      base_url: form.base_url || null,
      api_key: form.api_key,
      api_secret: form.api_secret || null,
    };
    if (form.ownerKind === "school") payload.school_id = form.ownerId;
    else payload.client_id = form.ownerId;

    try {
      await request("integrations/credentials", { method: "POST", payload });
      setForm((prev) => ({ ...prev, api_key: "", api_secret: "", ownerId: "" }));
      await loadCredentials();
    } catch (e) {
      setError(e?.detail || "Failed to create credential");
    }
  };

  const openEdit = (credential) => {
    setEditing(credential);
    setEditForm(EMPTY_EDIT);
    setError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(EMPTY_EDIT);
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    setError(null);
    const payload = {};
    if (editForm.base_url !== "") payload.base_url = editForm.base_url;
    if (editForm.api_key !== "") payload.api_key = editForm.api_key;
    if (editForm.api_secret !== "") payload.api_secret = editForm.api_secret;

    try {
      await request(`integrations/credentials/${editing.id}`, {
        method: "PATCH",
        payload,
      });
      closeEdit();
      await loadCredentials();
    } catch (e) {
      setError(e?.detail || "Failed to update credential");
    }
  };

  const testConnection = async (credential) => {
    setTestingId(credential.id);
    setTestResults((prev) => ({ ...prev, [credential.id]: undefined }));
    try {
      const result = await request(
        `integrations/credentials/${credential.id}/test`,
        { method: "POST" }
      );
      setTestResults((prev) => ({ ...prev, [credential.id]: result }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [credential.id]: { ok: false, detail: e?.detail || "Request failed" },
      }));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={submit}>
        <Form.Group className="mb-3" controlId="provider">
          <Form.Label>Provider</Form.Label>
          <Form.Control as="select" value={form.provider} onChange={setField("provider")}>
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-3" controlId="environment">
          <Form.Label>Environment</Form.Label>
          <Form.Control as="select" value={form.environment} onChange={setField("environment")}>
            {ENVIRONMENTS.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-3" controlId="ownerKind">
          <Form.Label>Owner type</Form.Label>
          <Form.Control
            as="select"
            value={form.ownerKind}
            onChange={(event) => {
              const { value } = event.target;
              setForm((prev) => ({ ...prev, ownerKind: value, ownerId: "" }));
            }}
          >
            {OWNER_KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-3" controlId="ownerId">
          <Form.Label>{form.ownerKind === "school" ? "School" : "Client"}</Form.Label>
          <Form.Control as="select" value={form.ownerId} onChange={setField("ownerId")}>
            <option value="">Select a {form.ownerKind}…</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-3" controlId="base_url">
          <Form.Label>Endpoint</Form.Label>
          <Form.Control value={form.base_url} onChange={setField("base_url")} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="api_key">
          <Form.Label>Client ID</Form.Label>
          <Form.Control
            value={form.api_key}
            onChange={setField("api_key")}
          />
        </Form.Group>
        <Form.Group className="mb-4" controlId="api_secret">
          <Form.Label>Secret</Form.Label>
          <Form.Control
            type="password"
            value={form.api_secret}
            onChange={setField("api_secret")}
          />
        </Form.Group>
        <Button type="submit">Create credential</Button>
      </Form>

      <Table striped bordered hover style={{ marginTop: "32px" }}>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Environment</th>
            <th>Owner</th>
            <th>Endpoint</th>
            <th>Active</th>
            <th>Has secret</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {credentials.map((c) => (
            <tr key={c.id}>
              <td>{c.provider}</td>
              <td>{c.environment}</td>
              <td>{ownerName(c)}</td>
              <td>{c.base_url}</td>
              <td>{String(c.is_active)}</td>
              <td>{String(c.has_api_secret)}</td>
              <td>
                <Button size="sm" variant="outline-secondary" onClick={() => openEdit(c)}>
                  Edit
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={testingId === c.id}
                  onClick={() => testConnection(c)}
                >
                  {testingId === c.id ? "Testing…" : "Test connection"}
                </Button>
                {testResults[c.id] && (
                  <div
                    className={
                      testResults[c.id].ok ? "text-success mt-1" : "text-danger mt-1"
                    }
                  >
                    {testResults[c.id].ok
                      ? `OK — ${testResults[c.id].data_source_count} data source(s)`
                      : `Failed: ${testResults[c.id].detail || "unknown error"}`}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={editing !== null} onHide={closeEdit}>
        <Form onSubmit={submitEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit credential</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">
              Leave a field blank to keep its current value. Secrets are
              write-only and are never shown.
            </p>
            <Form.Group className="mb-3" controlId="edit_base_url">
              <Form.Label>Endpoint</Form.Label>
              <Form.Control
                value={editForm.base_url}
                placeholder={editing?.base_url || ""}
                onChange={setEditField("base_url")}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="edit_api_key">
              <Form.Label>Client ID</Form.Label>
              <Form.Control
                value={editForm.api_key}
                placeholder="(unchanged)"
                onChange={setEditField("api_key")}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="edit_api_secret">
              <Form.Label>Secret</Form.Label>
              <Form.Control
                type="password"
                value={editForm.api_secret}
                placeholder="(unchanged)"
                onChange={setEditField("api_secret")}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEdit}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};
